/* eslint-disable react/jsx-props-no-spreading */
import { FC, useEffect, useMemo } from 'react';
import { Typography, Box, Svg } from '@altermaind/web/ui';
import { useResponsiveStyle } from '@/_shared/hooks/useResponsiveStyle';
import { DialogContainer } from './DialogContainer';
import { DialogProps } from './types';

/**
 * Dialog Component
 *
 * The `Dialog` component renders a dialog window with customizable styles and behaviors.
 *
 * Conditionally renders a Dialog based on the open prop, this component should never been used standalone.
 * @see useDialog
 *
 * @param {string} name - The unique name of the dialog.
 * @param {string} title - `optional` - The title text displayed in the dialog.
 * @param {string} header - `optional` - The header text displayed at the top of the dialog.
 * @param {boolean} closeButton - `optional` - Determines if the close button is displayed (default: true).
 * @param {React.ReactNode} children - The content to be displayed inside the dialog.
 * @param {boolean} open - `optional` - Specifies whether the dialog is open (default: false).
 * @param {function} onClose - `optional` - Callback function triggered when the dialog is closed.
 * @param {boolean} avoidCloseOnBackDrop - `optional` - Prevents closing the dialog when clicking on the backdrop (default: false).
 * @param {object} backDropStyle - `optional` - Custom styles for the backdrop.
 * @param {object} titleStyle - `optional` - Custom styles for the title.
 * @param {object} headerStyle - `optional` - Custom styles for the header.
 * @param {object} dialogStyle - `optional` - Custom styles for the dialog container.
 * @param {boolean} avoidDestroy - `optional` - Prevents destroying the dialog on close (default: false).
 * @param {object} closeButtonProps - `optional` - Additional properties for the close button.
 * @param {function} destroyDialog - `optional` - Callback function to destroy the dialog instance.
 * @param {string} innerBoxTopMarginProp - `optional` - Custom top margin for the inner box.
 * @param {string} data-testid - `optional` - Test identifier for the dialog component.
 * @param {string} maxWidth - `optional` - Maximum width of the dialog.
 *
 * @returns {JSX.Element|null} The rendered dialog component or null if not open.
 *
 * @example
 * <Dialog
 *   name="exampleDialog"
 *   title="Example Dialog"
 *   header="Header Text"
 *   closeButton
 *   open={isOpen}
 *   onClose={handleClose}
 *   avoidCloseOnBackDrop={false}
 * >
 *   <p>Dialog Content</p>
 * </Dialog>
 */

export const Dialog: FC<DialogProps> = ({
  name,
  title,
  header,
  closeButton = true,
  children,
  open = false,
  onClose,
  avoidCloseOnBackDrop = false,
  backDropStyle = {},
  titleStyle = {},
  headerStyle = {},
  dialogStyle = {},
  avoidDestroy = false,
  closeButtonProps = {},
  destroyDialog,
  innerBoxTopMargin: innerBoxTopMarginProp,
  maxWidth = '1200px',
  'data-testid': testId,
  specialsComponentsInHeader,
}) => {
  const { customElement, width } = closeButtonProps;
  const innerBoxTopMargin = useMemo(() => {
    if (innerBoxTopMarginProp) {
      return innerBoxTopMarginProp;
    }
    if (!header) {
      return 'xxxl';
    }
    return 'l';
  }, [header, innerBoxTopMarginProp]);

  const { genericResponsiveValue } = useResponsiveStyle();

  useEffect(() => {
    return () => {
      if (destroyDialog && !avoidDestroy) {
        destroyDialog();
      }
    };
  }, [avoidDestroy, destroyDialog]);

  return open ? (
    <DialogContainer name={name}>
      <Box
        display='flex'
        width='100vw'
        height='100vh'
        bg={'rgba(0,0,0,0.7)' as never}
        justifyContent='center'
        alignItems='center'
        zIndex={9999}
        position='fixed'
        top={0}
        left={0}
        onClick={!avoidCloseOnBackDrop ? onClose : undefined}
        style={{ cursor: avoidCloseOnBackDrop ? 'auto' : 'pointer', overflow: 'hidden' }}
        {...backDropStyle}
        data-testid={testId}
      >
        <Box
          display='flex'
          justifyContent='center'
          alignItems='center'
          width='100%'
          height='100%'
          overflow='auto'
        >
          <Box
            mt='none'
            display='flex'
            pb='m'
            bg='secondary/background'
            borderRadius='s'
            position='relative'
            elevation={3}
            flexDirection='column'
            onClick={e => e.stopPropagation()}
            style={{
              cursor: 'auto',
              maxHeight: '95vh',
              overflowY: 'auto',
              ...(dialogStyle?.style || {}),
            }}
            m={genericResponsiveValue({
              breakPoints: { desktop: 'xl' },
              defaultValue: 'xs',
            })}
            flex='1'
            width={genericResponsiveValue({
              breakPoints: { desktop: 'fit-content' },
              defaultValue: 'fit-content',
            })}
            maxWidth={genericResponsiveValue({
              breakPoints: { desktop: String(maxWidth) },
              defaultValue: '100%',
            })}
            {...dialogStyle}
          >
            <Box display='flex' justifyContent='center' alignItems='flex-start' flex={1} px='none'>
              {header && (
                <Box display='flex' flex={1} flexDirection='column' position='relative'>
                  <Typography variant='p1/bold' mt='m' textAlign='center' mb='s' {...headerStyle}>
                    {header}
                  </Typography>
                  <Box height={1} width='100%' backgroundColor='light' />
                </Box>
              )}
            </Box>

            {closeButton || specialsComponentsInHeader ? (
              <Box
                position='absolute'
                top='m'
                right='m'
                left='m'
                zIndex={9999}
                display='flex'
                alignItems='center'
              >
                {specialsComponentsInHeader || null}
                {closeButton ? (
                  <Box
                    width={width || 'ls'}
                    height='s'
                    onClick={onClose}
                    style={{ cursor: 'pointer' }}
                    marginLeft='auto'
                  >
                    {customElement || <Svg name='close' size='s' />}
                  </Box>
                ) : null}
              </Box>
            ) : null}

            <Box
              display='flex'
              justifyContent='center'
              alignItems='center'
              flexDirection='column'
              mt={innerBoxTopMargin}
              px={genericResponsiveValue({
                breakPoints: { desktop: 'l' },
                defaultValue: 'xs',
              })}
            >
              {title && (
                <Typography variant='h3' mt='none' textAlign='center' mb='s' {...titleStyle}>
                  {title}
                </Typography>
              )}
              {children}
            </Box>
          </Box>
        </Box>
      </Box>
    </DialogContainer>
  ) : null;
};

Dialog.displayName = 'Dialog';
